import stylex from '@stylexjs/stylex';
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { styles } from './styles';
import { ActivitySwitcher } from '../activity-switcher';
import { HeaderActions } from '../header-actions';
import { ModalAddOrganizer } from '@features/modal-organizer';
import { OranizerTab } from '@features/organizer-tab';
import { IOrganizer } from '@features/organizer/model/types';
import { SegmentsTab } from '@widgets/segments-tab';
import { ITab, Tabs } from '@shared/ui/tabs';
import { IEvent, IEventResponse } from '@pages/event/api/useFetchEvent';
import { ModalAddSegment } from '@features/add-segment/ui/modal-add-segment';
import { ModalTickets } from '@features/modal-sms';
import { CATEGORIES_TAB_ID, ORGANIZERS_TAB_ID, TABS_LIST } from '../../lib/constants';
import { useConfirm } from '@shared/hooks/useConfirm';
import { useFetchAddSegment } from '@pages/event/api/useFetchAddSegment';
import { useFetchDeleteSegment } from '@pages/event/api/useFetchDeleteSegment';
import { ADD_ORGANIZER_ERROR_MESSAGE, useFetchAddOrganizer } from '@pages/event/api/useFetchAddOrganizer';
import { useGetOrganizers } from '@pages/event/api/useGetOrganizers';
import {
  OrganizerChannel,
  getSendQrcodeToast,
  useSendAuthQrcodeToOrganizer,
} from '@pages/event/api/useSendAuthQrcodeToOrganizer';
import { getOrganizerFullName } from '@features/organizer/lib/getOrganizerFullName';
import { useJwtClaims } from '@features/auth';
import { useToast } from '@shared/hooks/useToast';
import { SettingsPopup } from '@features/settingsPopup';
import { useFetchDeleteEventOrganizer } from '@pages/event/api/useFetchDeleteOrg';
import { useUpdateEventMutation } from '@pages/event/api/useUpdateEventMutation';
import { CategoryTab } from '@features/category-tab';
import { ModalAddCategory } from '@features/add-category/ui';
import { Category, useGetTicketsByCategory } from '@pages/event/api/useFetchCategory';
import { ModalMoveToSegment } from '@features/madal-move-to-segment';
import { exportAllParticipants } from '@pages/event/api/exportAllParticipants';
import { useFilters } from '@pages/event/lib/filters-context.tsx';
import { useFetchTicketSegments, ISegment, TicketsFilterInput } from '@pages/event/api/useFetchTicketSegment';
import { useSendMessages } from '@pages/event/api/useSendMessages';
import { ModalInvitations } from '@features/modal-invitation';
import { LeadCollectorTab } from '@widgets/leadCollector';
import type { SendMessagesInput } from '@pages/event/api/useSendMessages';
import useGetSettingsByEventId from '@features/api/use-get-settings';
import { CBGroup, useAuth } from '@features/auth';
import type { EventSendInvitationsInput } from '@pages/event-send-invitations/lib/types';
import type { EventSendTicketsInput } from '@pages/event-send-tickets/lib/types';

interface EventBodyProps {
  event: IEventResponse | undefined;
  refetchEvent: () => void;
  onOpenSendInvitationsScreen: (selection: EventSendInvitationsInput) => void;
  onOpenSendTicketsScreen: (selection: EventSendTicketsInput) => void;
  isSettingsPopupVisible: boolean;
  onOpenSettings: () => void;
  onCloseSettings: () => void;
  checkedUsers: Record<number, number>;
  setCheckedUsers: React.Dispatch<React.SetStateAction<Record<number, number>>>;
}

export const EventBody: FC<EventBodyProps> = ({
  event,
  refetchEvent,
  onOpenSendInvitationsScreen,
  onOpenSendTicketsScreen,
  isSettingsPopupVisible,
  onOpenSettings,
  onCloseSettings,
  checkedUsers,
  setCheckedUsers,
}) => {
  const {
    data: { groupName },
  } = useAuth();
  const { openConfirmDialog } = useConfirm();
  const [activeTab, setActiveTab] = useState<ITab>(TABS_LIST[0]);
  const [organizers, setOrganizers] = useState<IOrganizer[]>([]);
  const [showModalOranizer, setShowModalOranizer] = useState(false);
  const [showModalAddSegment, setShowModalAddSegment] = useState(false);
  const [showModalAddCategory, setShowModalAddCategory] = useState(false);

  const [usersSMS, setUsersSMS] = useState<{ users: number[]; tickets: number[]; isAdmin: boolean }>({
    users: [],
    tickets: [],
    isAdmin: false,
  });

  const [showModalTickets, setShowModalTickets] = useState(false);
  const [showModalInvitations, setShowModalInvitations] = useState(false);
  const [showModalStatus, setShowModalStatus] = useState(false);
  const [showModalMoveToSegment, setShowModalMoveToSegment] = useState(false);
  const [selectedTicketsForMove, setSelectedTicketsForMove] = useState<number[]>([]);
  const [segments, setSegments] = useState<ISegment[]>([]);
  const [segmentsFilterState, setSegmentsFilterState] = useState<TicketsFilterInput | undefined>(undefined);

  const segmentsFilter = useMemo(() => segmentsFilterState, [segmentsFilterState]);
  const [messageAttempt, setMessageAttempt] = useState<number | undefined>(undefined);
  const [categories, setCategories] = useState<Category[]>([]);
  const { filters } = useFilters();

  const [, updateEvent] = useUpdateEventMutation();
  const [currentEvent, setCurrentEvent] = useState<IEvent | undefined>(undefined);
  const [{ fetching: addOrganizerFetching }, addOrganizer] = useFetchAddOrganizer();
  const [{ error: addSegmentError, fetching: segmFetching }, addSegment] = useFetchAddSegment();
  const [, deleteSegment] = useFetchDeleteSegment();
  const [, deleteOrganizer] = useFetchDeleteEventOrganizer();
  const eventId = event?.getEventById.id;
  const { organizersList } = useGetOrganizers(eventId, activeTab.id !== ORGANIZERS_TAB_ID);
  const segmentRefetchMap = useRef<{ [segmentId: number]: () => void }>({});
  const {
    data: initialCategories,
    fetching: categoriesFetching,
    refetch: refetchCategories,
  } = useGetTicketsByCategory(eventId, activeTab.id !== CATEGORIES_TAB_ID);
  const {
    segments: fetchedSegments = [],
    refetch: refetchSegmentsQuery,
    fetching: segmentsFetching,
  } = useFetchTicketSegments(eventId ?? 0, segmentsFilter);

  const { fetching: sendMessagesEmailFetching, sendMessages: sendMessagesEmailMutation } = useSendMessages();

  // Организатору без телефона QR можно отправить только в VK Teams
  const hasOrganizerWithoutPhone = useMemo(
    () =>
      usersSMS.isAdmin &&
      usersSMS.users.some((userId) => {
        const organizer = organizers.find(({ id }) => id === userId);
        return !organizer?.phone?.trim();
      }),
    [usersSMS, organizers]
  );
  const { data: eventSettings } = useGetSettingsByEventId(eventId ?? 0);

  useEffect(() => {
    setSegments(fetchedSegments);
  }, [fetchedSegments]);

  useEffect(() => {
    if (!event) return;
    setCurrentEvent(event.getEventById);
  }, [event]);

  useEffect(() => {
    if (initialCategories?.getTicketsByCategory) {
      setCategories(initialCategories.getTicketsByCategory);
    }
  }, [initialCategories]);
  const { showToast } = useToast();

  useEffect(() => {
    if (!organizersList) return;
    setOrganizers(organizersList);
  }, [organizersList]);

  const { fetching: smsFetchingAdmin, sendAuthQrcode } = useSendAuthQrcodeToOrganizer();
  const { userId } = useJwtClaims();

  //обновление эвента
  const handleToggleActive = useCallback(async () => {
    if (!currentEvent) return;

    const newActiveState = !currentEvent.active;

    try {
      const { error } = await updateEvent({ id: currentEvent.id, active: newActiveState });

      if (error) {
        const message = error.message.includes("don't have access")
          ? 'У вас нет прав на изменение мероприятия'
          : 'Ошибка при обновлении события';

        showToast(message, 'error');
        return;
      }

      setCurrentEvent({ ...currentEvent, active: newActiveState });
      showToast('Настройки успешно сохранены', 'success');
    } catch (error) {
      showToast('Ошибка при обновлении события', 'error');
    }
  }, [currentEvent, updateEvent, showToast]);

  const handleCancelOrganizer = () => {
    setShowModalOranizer(false);
  };

  const handleCancelTickets = () => {
    setShowModalTickets(false);
  };
  // сегменты
  const handleAddSegment = async (title: string) => {
    if (!event) return;
    const res = await addSegment({ title, eventId: eventId!, description: '' });
    if (addSegmentError || res.data === undefined) {
      showToast('Произошла ошибка при добавлении сегмента', 'error');
      return;
    }
    refetchEvent();
    const created = res.data.createTicketSegment;
    const newSegment: ISegment = {
      id: created.id,
      title: created.title,
      eventId: eventId,
      countTicket: 0,
      tickets: [],
    };

    setSegments((prev) => [...prev, newSegment]);
    setShowModalAddSegment(false);
  };

  const handleCancelAddSegment = () => {
    setShowModalAddSegment(false);
  };

  const registerRefetch = (segmentId: number, refetchFn: () => void) => {
    segmentRefetchMap.current[segmentId] = refetchFn;
  };

  const refetchAllSegments = () => {
    refetchSegmentsQuery();
    Object.values(segmentRefetchMap.current).forEach((fn) => fn?.());
  };

  const handleCancelAddCategory = () => {
    setShowModalAddCategory(false);
  };
  const handleCancelMoveToSegment = () => {
    setShowModalMoveToSegment(false);
  };

  // отправка билетов организаторам (QR / SMS)
  const handleShowModalTickets = ({
    users,
    tickets = [],
    isAdmin = false,
  }: {
    users: number[];
    tickets?: number[];
    isAdmin?: boolean;
  }) => {
    setUsersSMS({ users, tickets, isAdmin });
    setShowModalTickets(true);
  };

  const handleSendTickets = (users: number[], tickets: number[] = []) => {
    onOpenSendTicketsScreen({ ticketIds: tickets, userIds: users });
  };

  const handleSendInvitations = (users: number[], tickets: number[] = []) => {
    onOpenSendInvitationsScreen({ ticketIds: tickets, userIds: users });
  };

  // Старый флоу отправки приглашений
  //отправка приглашений
  // const handleShowModalInvitations = ({
  //   users,
  //   tickets = [],
  //   isAdmin = false,
  // }: {
  //   users: number[];
  //   tickets?: number[];
  //   isAdmin?: boolean;
  //   contentType?: SendContentType;
  // }) => {
  //   setUsersSMS({ users, tickets, isAdmin });
  //   setShowModalInvitations(true);
  // };

  const handleOpenStatus = () => {
    setShowModalStatus(true);
  };

  const handleCloseStatus = () => {
    setShowModalTickets(false);
    setShowModalStatus(false);
  };

  const sendQrcodeToOrganizer = async (message: string, channel: OrganizerChannel) => {
    if (!event) return;

    const outcome = await sendAuthQrcode({
      message,
      users: usersSMS.users[0] ? usersSMS.users : [],
      eventId: eventId!,
      channel,
    });

    setShowModalTickets(false);
    showToast(...getSendQrcodeToast(outcome));
  };

  const sendMessages = async (input: SendMessagesInput) => {
    try {
      const res = await sendMessagesEmailMutation(input);

      const result = res.data?.sendMessages;

      setMessageAttempt(result?.attempt);
    } catch (error) {
      showToast('Произошла ошибка при отправке сообщений', 'error', {
        duration: 10000,
        closable: true,
      });
      throw error;
    }
  };

  const resetAttempts = () => {
    setMessageAttempt(undefined);
  };

  /// организаторы
  const handleAddOrganizer = async (newOrganizer: Omit<IOrganizer, 'id'>) => {
    if (!event) return;
    const input = {
      eventId: eventId!,
      phone: newOrganizer.phone?.trim() || undefined,
      firstName: newOrganizer.firstName,
      lastName: newOrganizer.lastName,
    };
    const res = await addOrganizer({ input });
    if (res.error) {
      showToast(ADD_ORGANIZER_ERROR_MESSAGE, 'error');
      return;
    }
    setOrganizers([...organizers, { ...newOrganizer, id: res.data?.createEventOrganizer.id }]);
    setShowModalOranizer(false);
  };
  const handleDeleteOrganizer = async (organizer: IOrganizer) => {
    if (!event || organizer.id === undefined) return;
    const res = await deleteOrganizer({ userId: organizer.id, eventId: eventId! });
    if (res.data && res.data.deleteEventOrganizer.rowsAffected > 0) {
      setOrganizers(organizers.filter((item) => item.id !== organizer.id));
    } else {
      showToast('Произошла ошибка при удалении организатора', 'error');
    }
  };

  /**
   * Удаление сегмента
   * @param segment
   */
  const handleDeleteSegment = async (segment: ISegment) => {
    if (await openConfirmDialog(`Удалить сегмент ${segment.title} ?`, 'Удаление сегмента', 'Удалить')) {
      const res = await deleteSegment({ segmentId: segment.id });
      if (res.data !== undefined && res.data.deleteTicketSegment.success) {
        setSegments(segments.filter((item) => item.id !== segment.id));
        return;
      }
      showToast('Произошла ошибка при удалении сегмента', 'error');
    }
  };
  const eventName = typeof currentEvent?.name === 'string' ? currentEvent.name : '';
  const truncatedTitle = eventName.length > 55 ? eventName.slice(0, 55) + '...' : eventName;

  const handleDeleteTicketLocal = (ticketId: number) => {
    setUsersSMS((prev) => ({ ...prev, tickets: prev.tickets.filter((id) => id !== ticketId) }));
    setSegments((prev) =>
      prev.map((seg) => {
        if (!seg.tickets) return seg;
        const next = seg.tickets.filter((t) => t.id !== ticketId);
        const wasTicketInSegment = next.length < seg.tickets.length;
        return {
          ...seg,
          tickets: next,
          countTicket: wasTicketInSegment ? seg.countTicket - 1 : seg.countTicket,
        };
      })
    );
    refetchEvent();
  };

  const handleExportAllParticipants = useCallback(async () => {
    try {
      await exportAllParticipants({ ...filters, event_id: eventId });
    } catch (error) {
      console.error('Ошибка экспорта участников:', error);
    }
  }, [filters, eventId]);

  const ticketsTotal = event?.getEventById?.ticketsMetadata?.total ?? 0;
  const eventGroups = event?.getEventById?.groups ?? [];
  const isCBGroup = eventGroups.length ? eventGroups.some((group) => group.name === CBGroup) : groupName === CBGroup;

  const {
    isPendingTickets,
    isPendingInvites,
    totalInvites = 0,
    countAcceptedInvites = 0,
    countDeclinedInvites = 0,
  } = event?.getEventById?.metadata ?? {};

  const statusText =
    isPendingTickets && isPendingInvites
      ? 'Идёт отправка билетов и приглашений'
      : isPendingTickets
        ? 'Идёт отправка билетов'
        : isPendingInvites
          ? 'Идёт отправка приглашений'
          : undefined;

  const TABS: ReactNode[] = [
    <SegmentsTab
      segments={segments}
      onDeleteSegment={handleDeleteSegment}
      onAddSegment={() => setShowModalAddSegment(true)}
      sendTickets={handleSendTickets}
      sendInvitations={handleSendInvitations}
      onTicketDelete={handleDeleteTicketLocal}
      onMoveToSegment={(tickets) => {
        setSelectedTicketsForMove(tickets);
        setShowModalMoveToSegment(true);
      }}
      registerRefetch={registerRefetch}
      refetchSegmentsQuery={refetchSegmentsQuery}
      eventId={eventId}
      refetchCategories={refetchCategories}
      eventDateEnd={event?.getEventById.dateEnd}
      eventDateStart={event?.getEventById.dateStart}
      eventSettings={eventSettings}
      onFilterChange={setSegmentsFilterState}
      refetchEvent={refetchEvent}
      segmentsFetching={segmentsFetching}
      hasGeneratedLinks={event?.getEventById.hasGeneratedLinks}
      formId={event?.getEventById.formId ?? event?.getEventById.formData?.id ?? null}
      isCBGroup={isCBGroup}
      checkedUsers={checkedUsers}
      setCheckedUsers={setCheckedUsers}
    />,
    <OranizerTab
      organizers={organizers}
      handleAddOrganizer={() => setShowModalOranizer(true)}
      handleDelete={handleDeleteOrganizer}
      sendTickets={(users) => handleShowModalTickets({ users, isAdmin: true })}
      eventId={eventId}
    />,
    <CategoryTab
      handleAdd={() => setShowModalAddCategory(true)}
      categories={categories}
      fetching={categoriesFetching}
      refetch={refetchCategories}
      setCategories={setCategories}
      eventId={eventId ?? 1}
      refetchSegments={refetchSegmentsQuery}
      eventDateEnd={event?.getEventById.dateEnd}
      eventDateStart={event?.getEventById.dateStart}
      refetchEvent={refetchEvent}
      isCBGroup={isCBGroup}
      eventSettings={eventSettings}
    />,
    <LeadCollectorTab
      selectedForm={event?.getEventById.formData}
      refetchEvent={refetchEvent}
      hasGeneratedLinks={event?.getEventById.hasGeneratedLinks}
      formLinks={event?.getEventById.formLinks}
    />,
  ];
  return (
    <>
      <ModalAddOrganizer
        active={showModalOranizer}
        closeHandle={handleCancelOrganizer}
        addOrganizer={handleAddOrganizer}
        fetching={addOrganizerFetching}
      />
      <ModalAddSegment
        active={showModalAddSegment}
        closeHandle={handleCancelAddSegment}
        addSegment={handleAddSegment}
        segmFetching={segmFetching}
      />
      <ModalAddCategory
        eventId={eventId}
        active={showModalAddCategory}
        closeHandle={handleCancelAddCategory}
        refetch={refetchCategories}
        isCBGroup={isCBGroup}
      />
      <ModalInvitations
        active={showModalInvitations}
        closeHandle={() => setShowModalInvitations(false)}
        sendMessages={sendMessages}
        usersSMS={usersSMS}
        eventId={eventId}
        eventDateEnd={event?.getEventById.dateEnd}
        messageAttempt={messageAttempt}
        totalInvites={totalInvites}
        resetAttempts={resetAttempts}
        onOpenSettings={onOpenSettings}
        refetchEvent={refetchEvent}
      />

      <ModalTickets
        isLoading={smsFetchingAdmin || sendMessagesEmailFetching}
        active={showModalTickets}
        closeHandle={handleCancelTickets}
        sendSMS={sendQrcodeToOrganizer}
        sendMessages={sendMessages}
        senderName={getOrganizerFullName(organizers, userId)}
        hasOrganizerWithoutPhone={hasOrganizerWithoutPhone}
        isAdmin={usersSMS.isAdmin}
        usersSMS={usersSMS}
        eventId={eventId}
        messageAttempt={messageAttempt}
        resetAttempts={resetAttempts}
        isStatusVisible={showModalStatus}
        onStatusOpen={handleOpenStatus}
        onStatusClose={handleCloseStatus}
      />
      <ModalMoveToSegment
        closeHandle={handleCancelMoveToSegment}
        active={showModalMoveToSegment}
        segments={segments}
        selectedTickets={selectedTicketsForMove}
        refetchSegments={refetchAllSegments}
      />
      <div {...stylex.props(styles.base)}>
        <div {...stylex.props(styles.headerTitle)}>
          <h1 {...stylex.props(styles.header)}>{truncatedTitle}</h1>
          {totalInvites > 0 && (
            <p {...stylex.props(styles.inviteStats)}>
              Приглашений принято: {countAcceptedInvites} из {ticketsTotal}. Отклонено: {countDeclinedInvites}
            </p>
          )}
        </div>
        <div {...stylex.props(styles.row)}>
          <ActivitySwitcher
            isActive={currentEvent?.active}
            dateStart={currentEvent?.dateStart}
            statusText={statusText}
            onToggle={handleToggleActive}
          />

          <HeaderActions onExport={handleExportAllParticipants} onSettingsClick={onOpenSettings} />
        </div>
        <Tabs activeTab={activeTab} size="medium" tabs={TABS_LIST} onChange={setActiveTab} />
        <div {...stylex.props(styles.tabs)}>{event && TABS[activeTab.id]}</div>
        <SettingsPopup
          isVisible={isSettingsPopupVisible}
          onClose={onCloseSettings}
          totalInvites={totalInvites}
          eventDateStart={event?.getEventById.dateStart}
        />
      </div>
    </>
  );
};
